import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useNavigate } from 'react-router-dom';
import { sanitizeInput } from '../../utils/sanitize';
import { getRandomHeadlineImage, getHeadlineImagePath } from '../../utils/headlineImages';
import SEO from '../../utils/SEO';
import GoogleAdSquare from '../googleads/GoogleAdSquare';

export default function CreateRoom() {
  const [playerName, setPlayerName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [randomImage, setRandomImage] = useState<string>("");
  const navigate = useNavigate();

  useEffect(() => {
    setRandomImage(getRandomHeadlineImage());
  }, []);

  const handleCreateRoom = async () => {
    const sanitizedName = sanitizeInput(playerName);
    if (!sanitizedName) {
      setError('Please enter your name');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch('/api/rooms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          playerName: sanitizedName,
          iconId: 0, // Default icon for room creator
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create room');
      }

      const data = await response.json();
      console.log('Successfully created room:', data);
      navigate(`/app/room/${data.roomId}`, {
        state: { 
          isAdmin: true, 
          playerName: data.playerName, // Use name from backend for consistency
          playerId: data.playerId,
          iconId: 0
        }
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create room');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPlayerName(sanitizeInput(e.target.value));
  };

  const handleBack = () => {
    window.location.replace('/');
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <SEO 
        title="Create Game Room"
        description="Create a new Spyfall game room. Set up your game preferences, choose the round duration, and invite friends to join this thrilling social deduction game."
        keywords="create spyfall game, host game, game settings, multiplayer setup"
        noIndex={true}
      />
            {/* Google AdSense Ad */}
            <GoogleAdSquare />
      <div className="max-w-md mx-auto px-4 py-16">
        {/* Image Card */}
        <div className="relative w-64 h-64 bg-gradient-to-br from-red-900 to-red-700 rounded-lg shadow-lg p-6 flex flex-col items-center justify-center text-center border-2 border-red-500 overflow-hidden mb-8 mx-auto">
          <img
            src={getHeadlineImagePath(randomImage)}
            alt="Create Room"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute inset-[3px] bg-gray-900/20 rounded-md flex flex-col items-center justify-center p-4">
            <div className="text-red-500 font-bold text-sm mb-2"></div>
            <div className="text-xl font-bold"></div>
          </div>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Create New Room</h1>
          <p className="text-gray-400">Enter your name and click create room to start playing Spyfall with friends!</p>
        </div>

        <div className="space-y-6">
          <div>
            <label htmlFor="playerName" className="block text-sm font-medium text-gray-300 mb-2">
              Your Name
            </label>
            <Input
              id="playerName"
              type="text"
              value={playerName}
              onChange={handleNameChange}
              className="w-full bg-gray-800 border-gray-700 text-white"
              placeholder="Enter your name"
              required
              disabled={isLoading}
            />
          </div>

          

          {error && (
            <div className="text-red-500 text-sm text-center">
              {error}
            </div>
          )}

          <div className="flex gap-4">
            <Button
              className="flex-1 bg-red-500 hover:bg-red-600"
              onClick={handleCreateRoom}
              disabled={!playerName.trim() || isLoading}
            >
              {isLoading ? 'Creating...' : 'Create Room'}
            </Button>
            <Button
              className="flex-1 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              variant="outline"
              onClick={handleBack}
              disabled={isLoading}
            >
              Back
            </Button>
          </div>

          {/* Google AdSense Ad (Bottom) */}
          <div className="mt-6">
            <GoogleAdSquare />
          </div>
        </div>


      </div>
    </div>
  );
} 